import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/Hooks'
import Tile from '../layout/Tile'
import RouteHeader from '../controls/RouteHeader'
import { Box, Chip, Typography, useTheme, Popover } from '@mui/material'
import ContractsManager, { CSIFieldOption } from '../../managers/ContractsManager'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import CompareTable from '../controls/data_visualization/CompareTable'
import { setSelectedFields } from '../../store/slices/SessionSlice'
import { NestedKeyOf } from '../../definitions/Utility'
import { IContract } from '../../definitions/Contract'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'
import contract_fields_csi from '../../configuration_data/contract_fields_csi.json'
import { ContractCategoryOption, ContractField } from './ContractAnalysis'

type FieldOption = NestedKeyOf<IContract> | 'all'

const CompareContractsCsi: React.FC = () => {
    const dispatch = useAppDispatch()
    const routeParams = useAppSelector(state => state.session.routeParams)
    const selectedFields = useAppSelector(state => state.session.selectedFields || [])
    const fromContract = routeParams?.fromContract
    const toContract = routeParams?.toContract
    const commonFields = routeParams?.commonFields || []
    const csiValue = routeParams?.csiScore || 0
    const theme = useTheme()

    const allConfiguredFields = useMemo(() => {
        const allFields: { field: string, label: string }[] = []
        contract_fields_csi.forEach((category: ContractCategoryOption) => {
            category.fields.forEach((f: ContractField) => {
                allFields.push({ field: f.key, label: f.label })
            })
        })
        return allFields
    }, [])

    const percentages = useMemo(() => {
        const data = ContractsManager.fieldPercentages(fromContract, toContract, commonFields)
        const mapPerc: Record<string, number> = {}
        data.forEach(d => {
            mapPerc[d.field] = isNaN(d.percentage) ? 0 : d.percentage
        })
        return mapPerc
    }, [fromContract, toContract, commonFields])

    const fieldData = useMemo(() => {
        return allConfiguredFields.map(fd => ({
            field: fd.field,
            label: ContractsManager.getFieldLabel(fd.field) || fd.label || fd.field,
            percentage: percentages[fd.field] !== undefined ? percentages[fd.field] : 0
        }))
    }, [allConfiguredFields, percentages])

    const totalFields = useMemo(() => fieldData.length, [fieldData])
    const differentFields = totalFields - commonFields.length
    const averagePercentage = fieldData.reduce((sum, field) => sum + field.percentage, 0) / (totalFields || 1)

    const selectedTermsLabel = useMemo(() => {
        return selectedFields.includes('all')
            ? fieldData.map(fd => fd.label)
            : selectedFields.map(sf => ContractsManager.getFieldLabel(sf) || sf)
    }, [selectedFields, fieldData])

    const [fieldsAnchorEl, setFieldsAnchorEl] = useState<null | HTMLElement>(null)
    const [items, setItems] = useState<TreeViewBaseItem[]>([])
    const [selectedNodes, setSelectedNodes] = useState<string[]>([])
    const termsRef = useRef<HTMLDivElement>(null)
    const [popoverWidthTerms, setPopoverWidthTerms] = useState<number | undefined>(undefined)

    const isFieldsPopoverOpen = Boolean(fieldsAnchorEl)

    const handleFieldsPopoverOpen = (anchor: HTMLElement) => {
        setFieldsAnchorEl(anchor)
    }

    const handleFieldsPopoverClose = () => {
        setFieldsAnchorEl(null)
    }

    useEffect(() => {
        if (isFieldsPopoverOpen && termsRef.current) {
            setPopoverWidthTerms(termsRef.current.clientWidth)
        }
    }, [isFieldsPopoverOpen])

    const buildTreeItemsFromContractFields = (): TreeViewBaseItem[] => {
        return contract_fields_csi.map((category: ContractCategoryOption) => ({
            id: category.id || category.label,
            label: category.label,
            children: category.fields.map((field: ContractField) => ({
                id: field.key,
                label: field.label
            }))
        })) as TreeViewBaseItem[]
    }

    const findItem = (list: TreeViewBaseItem[], id: string): TreeViewBaseItem | undefined => {
        for (const i of list) {
            if (i.id === id) return i
            if (i.children) {
                const found = findItem(i.children, id)
                if (found) return found
            }
        }
        return undefined
    }

    const getAllDescendants = (id: string, tree: TreeViewBaseItem[]): string[] => {
        const root = findItem(tree, id)
        const collectChildren = (item: TreeViewBaseItem): string[] => {
            let result: string[] = [item.id]
            if (item.children) {
                for (const child of item.children) {
                    result = result.concat(collectChildren(child))
                }
            }
            return result
        }
        return root ? collectChildren(root) : []
    }

    const getAllIds = (list: TreeViewBaseItem[]): string[] => {
        let result: string[] = []
        for (const i of list) {
            result.push(i.id)
            if (i.children) {
                result = result.concat(getAllIds(i.children))
            }
        }
        return result
    }

    useEffect(() => {
        const treeItems = buildTreeItemsFromContractFields()
        setItems(treeItems)
        const allIds = getAllIds(treeItems) as FieldOption[]
        if (selectedFields.length === 0) {
            dispatch(setSelectedFields(allIds))
        }
    }, [dispatch])

    useEffect(() => {
        if (selectedFields.includes('all')) {
            const allIds = getAllIds(items)
            setSelectedNodes(allIds)
        } else {
            setSelectedNodes(selectedFields)
        }
    }, [selectedFields, items])

    const handleNodeSelect = (nodeIds: string[]) => {
        setSelectedNodes(nodeIds)
        const fieldsToSet = nodeIds.length === 0 ? ['all'] : (nodeIds as FieldOption[])
        dispatch(setSelectedFields(fieldsToSet as FieldOption[]))
        handleFieldsPopoverClose()
    }

    // TODO: fixed type error on fd.field as CSIFieldOption
    const filteredFieldData = useMemo(() => {
        if (selectedFields.includes('all')) {
            return fieldData
        } else {
            return fieldData.filter(fd => selectedFields.includes(fd.field as CSIFieldOption))
        }
    }, [fieldData, selectedFields])

    const handleDeleteTerm = (category: string) => {
        const fieldToRemove = selectedFields.find(field => (ContractsManager.getFieldLabel(field) || field) === category)
        if (fieldToRemove) {
            const updatedFields = selectedFields.filter(field => field !== fieldToRemove)
            dispatch(setSelectedFields(updatedFields.length === 0 ? ['all'] : updatedFields as FieldOption[]))
        }
    }
    const fromContractName = fromContract?.name || 'Unnamed Contract'
    const toContractName = toContract?.name || 'Unnamed Contract'

    return (
        <Tile.Container>
            <RouteHeader primary='Contracts Cross Similarity' />
            <Tile.Row style={{ marginTop: '16px' }}>
                <Tile.Column style={{ gap: '40px' }}>
                    <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography mb={2} variant='h6' fontWeight='bold'>Selected Contracts</Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: `1px solid ${theme.palette.grey[700]}`,
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    padding: 1,
                                    height: 200
                                }}>
                                <Box sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Chip label={fromContractName} color='default' />
                                    <Chip label={toContractName} color='default' />
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography mb={2} variant='h6' fontWeight='bold'>Select Terms</Typography>
                            <Box
                                ref={termsRef}
                                onClick={e => handleFieldsPopoverOpen(e.currentTarget)}
                                sx={{
                                    'display': 'flex',
                                    'flexDirection': 'column',
                                    'border': `1px solid ${theme.palette.grey[700]}`,
                                    'borderRadius': 2,
                                    'boxShadow': 1,
                                    'padding': 1,
                                    'height': 200,
                                    'justifyContent': 'flex-start',
                                    'overflow': 'auto',
                                    'cursor': 'pointer',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover
                                    }
                                }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                    {selectedTermsLabel.map((category, index) => (
                                        <Chip
                                            key={index}
                                            label={category}
                                            color='default'
                                            onDelete={() => handleDeleteTerm(category)}
                                        />
                                    ))}
                                </Box>
                            </Box>
                            <Popover
                                open={isFieldsPopoverOpen}
                                anchorEl={fieldsAnchorEl}
                                onClose={handleFieldsPopoverClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{ style: { width: popoverWidthTerms, maxHeight: '400px', overflow: 'auto' } }}>
                                <Box sx={{ p: 2 }}>
                                    <RichTreeView
                                        sx={{
                                            '.Mui-selected': { backgroundColor: 'transparent !important' },
                                            '.Mui-selected:hover': { backgroundColor: 'transparent !important' }
                                        }}
                                        multiSelect
                                        checkboxSelection
                                        items={items}
                                        selectedItems={selectedNodes}
                                        onSelectedItemsChange={(event, newSelectedItems) => {
                                            const added = newSelectedItems.filter(id => !selectedNodes.includes(id))
                                            const removed = selectedNodes.filter(id => !newSelectedItems.includes(id))
                                            for (const a of added) {
                                                const descendants = getAllDescendants(a, items)
                                                for (const d of descendants) {
                                                    if (!newSelectedItems.includes(d)) {
                                                        newSelectedItems.push(d)
                                                    }
                                                }
                                            }
                                            for (const r of removed) {
                                                const descendants = getAllDescendants(r, items)
                                                for (const d of descendants) {
                                                    const index = newSelectedItems.indexOf(d)
                                                    if (index > -1) {
                                                        newSelectedItems.splice(index, 1)
                                                    }
                                                }
                                            }
                                            const unique = Array.from(new Set(newSelectedItems))
                                            handleNodeSelect(unique)
                                        }}
                                    />
                                </Box>
                            </Popover>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography mb={2} variant='h6' fontWeight='bold'>AI Summary</Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: `1px solid ${theme.palette.grey[700]}`,
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    padding: 2,
                                    height: 200,
                                    justifyContent: 'center',
                                    overflow: 'auto'
                                }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography variant='body1' color='textSecondary' textAlign='center'>
                                        Coming soon
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box style={{ gap: '16px', display: 'flex' }}>
                        <SmartCard sx={{ flex: 1, padding: 3 }}>
                            <KPICard label='Features in Common' data={{ value: commonFields.length }} />
                        </SmartCard>
                        <SmartCard sx={{ flex: 1, padding: 3 }}>
                            <KPICard label='Different Features' data={{ value: differentFields }} />
                        </SmartCard>
                        <SmartCard sx={{ flex: 1, padding: 3 }}>
                            <KPICard
                                label='Average Similarity'
                                unit='%'
                                data={{ value: Number(averagePercentage.toFixed(2)) }}
                            />
                        </SmartCard>
                        <SmartCard sx={{ flex: 1, padding: 3 }}>
                            <KPICard
                                label='Total CSI Index'
                                unit='%'
                                data={{ value: csiValue.toFixed(2) }}
                            />
                        </SmartCard>
                    </Box>
                    <SmartCard sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 3, width: 'auto', overflowY: 'auto' }}>
                        <CompareTable
                            fromContract={fromContract}
                            toContract={toContract}
                            commonFields={commonFields}
                            fieldData={filteredFieldData}
                        />
                    </SmartCard>
                </Tile.Column>
            </Tile.Row>
        </Tile.Container>
    )
}

export default CompareContractsCsi
